import { useMemo, useState } from "react";
import { cn } from "@/utils/cn";

interface PencomSegmentedDatePickerProps {
  id: string;
  label: string;
  descriptionText?: string;
  value: string;
  onChange: (value: string) => void;
  max?: string;
  min?: string;
  error?: boolean;
  helperText?: string;
  labelClassName?: string;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DEFAULT_MIN_YEAR = 1900;

const daysInMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

const toISODate = (year: number, month: number, day: number) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const parseISODate = (value: string): [number | "", number | "", number | ""] => {
  if (!value) return ["", "", ""];
  const [yyyy, mm, dd] = value.split("-");
  return [Number(yyyy), Number(mm), Number(dd)];
};

const SegmentSelect = ({
  id,
  value,
  placeholder,
  options,
  onChange,
  error,
}: {
  id: string;
  value: number | "";
  placeholder: string;
  options: { value: number; label: string }[];
  onChange: (value: number) => void;
  error?: boolean;
}) => (
  <div
    className={cn(
      "relative border rounded-lg",
      error ? "border-red-500" : "border-stroke"
    )}
  >
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="appearance-none bg-transparent pl-4 pr-9 py-3 font-regular text-xs font-clash-display text-medium-dark-grey focus:outline-none focus:ring-2 focus:ring-primary-button focus:border-transparent rounded-lg"
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
      <img
        src="/assets/icons/dropdown-arrow.svg"
        alt="dropdown arrow"
        className="w-4 h-4"
      />
    </div>
  </div>
);

const PencomSegmentedDatePicker = ({
  id,
  label,
  descriptionText,
  value,
  onChange,
  max,
  min,
  error,
  helperText,
  labelClassName,
}: PencomSegmentedDatePickerProps) => {
  // Day/month/year are tracked independently so a partial selection (e.g.
  // day picked but month/year not yet) survives re-renders instead of being
  // wiped out by the parent's `value`, which only becomes a real date once
  // all three segments are chosen.
  const [[initialYear, initialMonth, initialDay]] = useState(() =>
    parseISODate(value)
  );
  const [selectedYear, setSelectedYear] = useState<number | "">(initialYear);
  const [selectedMonth, setSelectedMonth] = useState<number | "">(
    initialMonth
  );
  const [selectedDay, setSelectedDay] = useState<number | "">(initialDay);

  const maxDate = max ? new Date(`${max}T00:00:00`) : undefined;
  const minDate = min ? new Date(`${min}T00:00:00`) : undefined;
  const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear();
  const minYear = minDate ? minDate.getFullYear() : DEFAULT_MIN_YEAR;
  const maxMonth = maxDate ? maxDate.getMonth() + 1 : undefined;
  const minMonth = minDate ? minDate.getMonth() + 1 : undefined;
  const maxDay = maxDate ? maxDate.getDate() : undefined;
  const minDay = minDate ? minDate.getDate() : undefined;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = maxYear; y >= minYear; y--) list.push(y);
    return list;
  }, [maxYear, minYear]);

  const monthOptions = useMemo(() => {
    let months = MONTHS.map((name, idx) => ({ value: idx + 1, label: name }));
    if (maxMonth && selectedYear === maxYear) {
      months = months.filter((m) => m.value <= maxMonth);
    }
    if (minMonth && selectedYear === minYear) {
      months = months.filter((m) => m.value >= minMonth);
    }
    return months;
  }, [maxMonth, minMonth, selectedYear, maxYear, minYear]);

  const dayOptions = useMemo(() => {
    const year = selectedYear || maxYear;
    const month = selectedMonth || 1;
    const count = daysInMonth(year, month);
    let days = Array.from({ length: count }, (_, i) => i + 1);
    if (maxDay && selectedYear === maxYear && selectedMonth === maxMonth) {
      days = days.filter((d) => d <= maxDay);
    }
    if (minDay && selectedYear === minYear && selectedMonth === minMonth) {
      days = days.filter((d) => d >= minDay);
    }
    return days.map((d) => ({ value: d, label: String(d) }));
  }, [selectedYear, selectedMonth, maxYear, maxMonth, maxDay, minYear, minMonth, minDay]);

  const commit = (next: {
    day: number | "";
    month: number | "";
    year: number | "";
  }) => {
    const { day, month, year } = next;
    if (!day || !month || !year) {
      onChange("");
      return;
    }
    const clampedDay = Math.min(day, daysInMonth(year, month));
    onChange(toISODate(year, month, clampedDay));
  };

  const handleDayChange = (day: number) => {
    setSelectedDay(day);
    commit({ day, month: selectedMonth, year: selectedYear });
  };

  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    commit({ day: selectedDay, month, year: selectedYear });
  };

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    commit({ day: selectedDay, month: selectedMonth, year });
  };

  return (
    <div className="flex flex-col gap-y-1">
      <label
        htmlFor={id}
        className={cn(
          "font-clash-display text-medium-dark-grey text-md font-semibold",
          labelClassName
        )}
      >
        {label}
      </label>
      {descriptionText && (
        <p className="font-clash-display font-medium text-xs text-medium-grey tracking-[-0.06px]">
          {descriptionText}
        </p>
      )}
      <div className="flex items-center gap-3">
        <SegmentSelect
          id={id}
          value={selectedDay}
          placeholder="Day"
          options={dayOptions}
          onChange={handleDayChange}
          error={error}
        />
        <SegmentSelect
          id={`${id}-month`}
          value={selectedMonth}
          placeholder="Month"
          options={monthOptions}
          onChange={handleMonthChange}
          error={error}
        />
        <SegmentSelect
          id={`${id}-year`}
          value={selectedYear}
          placeholder="Year"
          options={years.map((y) => ({ value: y, label: String(y) }))}
          onChange={handleYearChange}
          error={error}
        />
      </div>
      {helperText && (
        <p className="text-red-500 text-md font-clash-display">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default PencomSegmentedDatePicker;
